import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Spinner, Button, Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Button as PButton } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTableBase } from '../../DataTables/DataTableBase';
import { useTranslation } from "react-i18next";
import {
    addTypeExclusionAction,
    updateDictionaireAction,
    deleteDictionaireAction,
    loadingToggleAction,
    downloadExclusionTypeTemplateAction
} from '../../../../store/actions/AdministrationAction';
import { 
    importProvincesFileUploadAction,
    startLoader
} from '../../../../store/actions/ProviderActions';

const TypeExclusionTab = ({ typesExclusions, showLoading, showProvLoading, hasMenu, canDo, dispatch }) => {
    const { t } = useTranslation();
    let errorsObj = { name: '', code: '' };
    
    const [nameFr, setNameFr] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [code, setCode] = useState('');
    const [file, setFile] = useState(null);
    const [errors, setErrors] = useState(errorsObj);
    const [updateMode, setUpdateMode] = useState(false);
    const [dataUpdateId, setDataUpdateId] = useState(null);
    const [addModal, setAddModal] = useState(false);

    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            nameFr: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        });
        setGlobalFilterValue("");
    };

    const renderHeader = () => {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <PButton 
                    type="button" 
                    icon="pi pi-filter-slash"  
                    label={t('common.reinit')} 
                    outlined 
                    onClick={clearFilter}
                    className="me-2"
                    size="small"
                />
                <div className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        value={globalFilterValue} 
                        onChange={(e) => onGlobalFilterChange(e)} 
                        placeholder={t('common.search')}
                        size="small"
                    />
                </div>
            </div>
        );
    };

    const clearFilter = () => {
        initFilters();
    };

    const setColumns = () => {
        return [
            {name: 'nameFr', headerName: t('common.name'), filterPlaceholder: t('common.filter-name'), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'createdAt', filterField: 'createdAt', dataType: 'date', headerName: t('common.created-on'), filterPlaceholder: t('common.filter-by-creation'), style: { minWidth: '6rem' }, filter: true, bodyTemplate: datePaiementBodyTemplate, filterElement: dateFilterTemplate},
            {name: 'actions', headerName: 'Actions', filterPlaceholder: '', style: { minWidth: '6rem'}, filter: false, bodyTemplate: getActionsTemplate},
        ]
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const getActionsTemplate = (rowData) => {
        return (
            <div className="d-flex justify-content-center gap-1">
                {hasMenu('MENPARAM') && canDo('MENPARAM', 'UPDATEPARAM') && (
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => updateData(rowData)}
                        title={t('common.edit')}
                    >
                        <i className="fa fa-pencil"></i>
                    </button>
                )}
                {hasMenu('MENPARAM') && canDo('MENPARAM', 'DELETEPARAM') && (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(rowData)}
                        title={t('common.delete')}
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                )}
            </div>
        );
    };

    const handleDelete = (rowData) => {
        swal({
            title: t('common.delete-the') + "type d'exclusion " + rowData.nameFr + " ?",
            text: t('common.once') + "type d'exclusion" + t('common.content-deleted'),
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                dispatch(deleteDictionaireAction(rowData.id, 'type-exclusion'));
            }
        });
    };

    const updateData = (data) => {
        setNameFr(data.nameFr || '');
        setNameEn(data.nameEn || '');
        setCode(data.code || '');
        setUpdateMode(true);
        setDataUpdateId(data.id);
        setAddModal(true);
    };

    const dateFilterTemplate = (options) => {
        return <Calendar 
            value={options.value} 
            onChange={(e) => options.filterCallback(e.value, options.index)} 
            dateFormat="dd/mm/yy" 
            placeholder="dd/mm/yyyy" 
            mask="99/99/9999" 
        />;
    };

    const datePaiementBodyTemplate = (rowData) => {
        return formatDate(rowData.createdAt);
    };

    const formatDate = (value) => {
        return value.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const resetForm = () => {
        setNameFr('');
        setNameEn('');
        setCode('');
        setFile(null);
        setErrors(errorsObj);
        setUpdateMode(false);
        setDataUpdateId(null);
    };

    const handleDownloadTemplate = () => {
        dispatch(downloadExclusionTypeTemplateAction());
    };

    useEffect(() => {
        initFilters();
    }, []);

    function onTypeExclusionSave(e) {
        e.preventDefault();
        let error = false;
        const errorObj = { ...errorsObj };
        
        if (file) {
            dispatch(startLoader());
            dispatch(importProvincesFileUploadAction(file));
            return;
        }
        
        if (nameFr === '') {
            errorObj.name = t('common.name-required');
            error = true;
        }
        
        setErrors(errorObj);
        if (error) {
            swal('Oops', t('common.check-form-fields'), "error");
            return;
        }
        
        dispatch(loadingToggleAction(true));

        if (updateMode) {
            let obj = { nameFr, code, nameEn: nameFr, reference: 'type-exclusion' };
            dispatch(updateDictionaireAction(dataUpdateId, obj, 'type-exclusion'));
        } else {
            let obj = { nameFr, code, nameEn: nameFr, reference: 'type-exclusion' };
            dispatch(addTypeExclusionAction(obj));
        }
        
        setAddModal(false);
        resetForm();
    }

    function selectFile(event) {
        setFile(event.target.files[0]);
    }

    return (
        <>
            {/* Header */}
            <div className="provider-type-header">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                        <h4 className="mb-1 text-white">{t('common.typ-motif-exl')}</h4>
                        <p className="mb-0 small text-white-50">{t('common.manage-exclusion-types')}</p>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <button
                            type="button"
                            className="btn btn-outline-light btn-sm d-flex align-items-center"
                            onClick={handleDownloadTemplate}
                            style={{ 
                                borderColor: 'rgba(255, 255, 255, 0.7)',
                                color: 'white',
                                fontWeight: '500'
                            }}
                        >
                            <i className="fa fa-download me-2"></i> 
                            {t("common.download-template")}
                        </button>
                        
                        {/* {hasMenu('MENPARAM') && canDo('MENPARAM', 'CREATEPARAM') && ( */}
                            <button
                                type="button"
                                className="btn btn-light btn-sm d-flex align-items-center"
                                onClick={() => {
                                    resetForm();
                                    setAddModal(true);
                                }}
                                style={{ 
                                    backgroundColor: 'white',
                                    color: '#4e73df',
                                    fontWeight: '600',
                                    border: 'none'
                                }}
                            >
                                <i className="fa fa-plus-circle me-2"></i> 
                                {t("common.new-exl")}
                            </button>
                        {/* )} */}
                    </div>
                </div>
            </div>

            {/* Modal for creating/editing type exclusion */}
            <Modal 
                show={addModal} 
                onHide={() => setAddModal(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="h5">
                        {updateMode ? t("common.update-exl") + nameFr : t("common.new-exl")}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="p-4">
                    <form>
                        <div className="mb-3">
                            <label className="form-label">{t('common.code')} <small className="text-muted">({t('common.optional')})</small></label>
                            <input
                                type="text"
                                className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                                placeholder="EXC"
                                value={code}
                                disabled={showLoading || showProvLoading || file}
                                onChange={(e) => setCode(e.target.value)}
                            />
                            {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                        </div>
                        
                        <div className="mb-3">
                            <label className="form-label">{t('common.name')}</label>
                            <input
                                type="text"
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                placeholder="Type d'exclusion"
                                value={nameFr}
                                disabled={showLoading || showProvLoading || file}
                                onChange={(e) => setNameFr(e.target.value)}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>
                        
                        <hr className="my-4" />
                        
                        <div className="mb-3">
                            <h6 className="text-primary mb-3">{t("common.import-csv-activity")}</h6>
                            <div className="input-group">
                                <span className="input-group-text">CSV</span>
                                <input 
                                    type="file" 
                                    className="form-control" 
                                    onChange={selectFile}
                                    accept=".csv"
                                />
                            </div>
                            {file && (
                                <div className="alert alert-info mt-2 py-2">
                                    <small>File selected: <strong>{file.name}</strong></small>
                                </div>
                            )}
                        </div>
                    </form>
                </Modal.Body>
                
                <Modal.Footer className="border-top">
                    <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => setAddModal(false)}
                        disabled={showLoading || showProvLoading}
                    >
                        {t('common.cancel')}
                    </button>
                    
                    {(showLoading || showProvLoading) ? (
                        <Button variant="primary" disabled>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            <span className="ms-2">{t('common.saving')}</span>
                        </Button>
                    ) : (
                        <button 
                            type="button" 
                            className="btn btn-primary"
                            onClick={onTypeExclusionSave}
                        >
                            {t('common.save')}
                        </button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* DataTable */}
            <div className="card border-0 shadow-sm bg-white">
                <div className="card-body p-0">
                    <DataTableBase 
                        data={typesExclusions ? typesExclusions.map(elt => {
                            elt.createdAt = new Date(elt.createdAt ? elt.createdAt : elt.created);
                            return elt;
                        }) : []}
                        emptyMessage="Aucun type d'exclusion trouvé"
                        filters={filters}
                        globalFiltersFields={['nameFr', 'createdAt']}
                        header={renderHeader()}
                        columns={setColumns()}
                        exportColumns={[
                            { title: t('common.name'), dataKey: 'nameFr' },
                            { title: t('common.create-date'), dataKey: 'createdAt' },
                        ]}
                        rows={10} 
                        showExport={true}
                        exportFilename="types-exclusion"
                        loading={showLoading}
                        lazy={false}
                    />
                </div>
            </div>
        </>
    );
};

export default TypeExclusionTab;